import { useSyncExternalStore } from 'react'

/**
 * 敏感图片「这一张现在要不要遮」的唯一真相源。
 *
 * 与覆盖层组件分开放，是因为它导出的是常量与 hook 而不是组件 ——
 * 混在一个文件里会让那个文件失去 React Fast Refresh。
 *
 * 哪些图要遮由**后端**说了算：资产行上的 `sensitive`（后端迁移 0037），
 * 经 `PostSummary.cover_sensitive`、`PublicPostDetail.gallery[].sensitive` 等字段
 * 送到前端。前端不猜、也不按标签或分级推断。
 */

/**
 * 加在被遮罩 `<img>` 上的类。
 *
 * 三个值各有各的理由：
 *
 *   - `blur-3xl`（64px）—— 半径要大到连构图都看不出来，不是「打个码」。
 *   - `brightness-[0.3]` —— 只糊不压暗的话，一张高调的图糊完仍然是一片亮色，
 *     大致轮廓照样认得出来。
 *   - `scale-150` —— CSS 的 `blur()` 会把边缘像素与透明混在一起，于是糊完的图
 *     四周透出一圈底色，在 `overflow-hidden` 的框里看就是一道浅边。放大一半把
 *     那圈脏边推到框外面去；图已经糊得看不出内容，多裁掉一点不损失任何东西。
 */
export const sensitiveImageClass = 'scale-150 blur-3xl brightness-[0.3]'

/** 一次会话内已确认展示的图片，按 URL 记。 */
const confirmed = new Set<string>()
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * 确认展示某一张图，只在**当前会话的内存里**生效。
 *
 * 不写 localStorage、不写 sessionStorage、也不上报后端：刷新页面回到遮罩状态是
 * 业主选定的粒度。一个人在自己的浏览器上点过什么，不该留下任何痕迹。
 *
 * 按 URL 而不是按「哪张卡片」记：同一张图出现在列表、详情页图集和正文里是常事，
 * 确认过一次之后那几处应当一起解开 —— 它们本来就是同一张图。
 */
export function confirmSensitiveImage(url: string): void {
  if (!url || confirmed.has(url)) return
  confirmed.add(url)
  for (const listener of listeners) listener()
}

/**
 * 这张图现在要不要遮。
 *
 * `sensitive` 为假时永远返回 false，连订阅都不必在意 —— 绝大多数图片走的是
 * 这条路。`url` 缺省（还没拿到封面）时同样不遮：没有图就没有什么要遮的。
 */
export function useSensitiveMask(url: string | undefined, sensitive: boolean | undefined): boolean {
  const isConfirmed = useSyncExternalStore(
    subscribe,
    () => (url ? confirmed.has(url) : true),
    // 服务端渲染没有「本会话确认过」这回事，一律按未确认算。
    () => false,
  )
  return Boolean(sensitive && url) && !isConfirmed
}
